import { HqlCode } from '@/components/hql/HqlCode';
import { WhenToApplyQuote } from '@/components/hql/WhenToApplyQuote';
import IdWithCopy from '@/components/IdWithCopy';
import { JsonViewer } from '@/components/JsonViewer';
import { MetadataItem } from '@/components/MetadataItem';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { appliesToDraftResultsState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { AppliesToDraftResult, AppliesToDraftResultId, ConsiderationType } from '@rock/types';
import { ArrowLeft } from 'lucide-react';
import type { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const AppliesToDraftResultEntry: FC<{ considerationType: ConsiderationType }> = ({ considerationType }) => {
    const { resultId } = useParams<{ resultId?: AppliesToDraftResultId }>();
    const [results, resultsLoadable] = useLoadableAtomValue(appliesToDraftResultsState(considerationType.id), []);
    const result = resultId ? results.find((r) => r.id === resultId) : undefined;

    if (!result) {
        return (
            <Card>
                <CardContent className="p-6">
                    {resultsLoadable.state === 'loading' ? 'Loading ...' : `Query draft not found (ID: ${resultId}).`}
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <DraftDetailsCard result={result} />
            <AiContextCard result={result} />
            <Card>
                <CardHeader>
                    <CardTitle>Result Object</CardTitle>
                    <CardDescription>The complete query draft result stored in Firestore.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                        <JsonViewer data={result} />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

const DraftDetailsCard: FC<{ result: AppliesToDraftResult }> = ({ result }) => {
    const navigate = useNavigate();
    return (
        <Card>
            <CardHeader className="border-b-1 pb-3 flex flex-row items-center">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <CardTitle className="text-xl">Query Draft</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                    <MetadataItem label="Result ID" value={<IdWithCopy id={result.id} variant="mono" />} />
                    <MetadataItem label="Created At" value={new Date(result.createdAt).toLocaleString()} />
                </div>
                <MetadataItem label="Drafted From" value={<WhenToApplyQuote text={result.whenToApply} />} />
                <MetadataItem
                    label="Query"
                    value={
                        result.result.query ? (
                            <HqlCode query={result.result.query} block />
                        ) : (
                            <span className="text-muted-foreground">None drafted</span>
                        )
                    }
                />
                {result.validationErrors.length > 0 ? (
                    <MetadataItem
                        label="Validation Errors"
                        value={<Bullets items={result.validationErrors} tone="text-destructive" />}
                    />
                ) : null}
                {result.validationWarnings?.length ? (
                    <MetadataItem
                        label="Validation Warnings"
                        value={<Bullets items={result.validationWarnings} tone="text-amber-700" />}
                    />
                ) : null}
                {result.result.issue ? (
                    <MetadataItem
                        label="Issue"
                        value={<div className="whitespace-pre-wrap text-amber-700">{result.result.issue}</div>}
                    />
                ) : null}
                <MetadataItem
                    label="Summary"
                    value={<div className="whitespace-pre-wrap">{result.result.summary}</div>}
                />
            </CardContent>
        </Card>
    );
};

const Bullets: FC<{ items: string[]; tone: string }> = ({ items, tone }) => (
    <div className="space-y-1">
        {[...new Set(items)].map((item) => (
            <div key={item} className={`whitespace-pre-wrap ${tone}`}>
                • {item}
            </div>
        ))}
    </div>
);

const AiContextCard: FC<{ result: AppliesToDraftResult }> = ({ result }) => (
    <Card>
        <CardHeader className="border-b-1 pb-3">
            <CardTitle className="text-xl">AI Context</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
                <MetadataItem label="Platform" value={result.aiContext.platform} />
                <MetadataItem label="Model" value={result.aiContext.model} />
                <MetadataItem label="Task Version" value={`${result.aiContext.taskVersion}`} />
            </div>
            <Message label="System Message" text={result.aiContext.systemMessage} />
            <Message label="User Message" text={result.aiContext.userMessage} />
        </CardContent>
    </Card>
);

const Message: FC<{ label: string; text: string }> = ({ label, text }) => (
    <div className="flex flex-col gap-2">
        <div className="text-sm text-muted-foreground">{label}</div>
        <pre className="whitespace-pre-wrap text-xs bg-muted p-4 rounded-md overflow-x-auto">{text}</pre>
    </div>
);
